import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
  CreditCard,
  QrCode,
  ExternalLink,
  Copy,
  Trash2,
  Loader2,
  Receipt,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/table";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const SITUACAO = {
  open: { rotulo: "Em aberto", variante: "outline-solid" },
  paid: { rotulo: "Paga", variante: "secondary" },
  overdue: { rotulo: "Atrasada", variante: "destructive" },
  canceled: { rotulo: "Cancelada", variante: "outline-solid" },
};

const CICLO = {
  monthly: "Mensal",
  quarterly: "Trimestral",
  yearly: "Anual",
};

const moeda = (valor) =>
  Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

// Vencimento vem do banco como meia-noite UTC. Em fuso negativo, converter
// para hora local joga a data para o dia anterior — por isso a parte da data
// é lida literalmente, sem passar por Date.
const dataCurta = (valor) => {
  if (!valor) return "—";

  const soData = String(valor).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(soData)) {
    const [ano, mes, dia] = soData.split("-");
    return `${dia}/${mes}/${ano}`;
  }

  try {
    return format(parseISO(valor), "dd/MM/yyyy");
  } catch {
    return "—";
  }
};

// Cartão de resumo do topo. Só texto e um rótulo — sem gráfico, porque aqui
// o que importa é o número exato, não a tendência.
const Resumo = ({ rotulo, valor, detalhe }) => (
  <div className="rounded-xl border bg-card p-4">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">
      {rotulo}
    </p>
    <p className="mt-1 text-xl font-semibold tabular-nums">{valor}</p>
    {detalhe && (
      <p className="mt-1 text-xs text-muted-foreground">{detalhe}</p>
    )}
  </div>
);

const Billing = () => {
  const [assinatura, setAssinatura] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pagando, setPagando] = useState(null);
  const [cartaoOpen, setCartaoOpen] = useState(false);
  const [salvandoCartao, setSalvandoCartao] = useState(false);
  const [confirmRemover, setConfirmRemover] = useState(false);

  const [cartao, setCartao] = useState({
    holderName: "",
    number: "",
    expiryMonth: "",
    expiryYear: "",
    ccv: "",
    name: "",
    email: "",
    cpfCnpj: "",
    postalCode: "",
    addressNumber: "",
    phone: "",
  });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [resAssinatura, resFaturas] = await Promise.all([
        api.get("/subscriptions/mine"),
        api.get("/invoices/mine"),
      ]);
      setAssinatura(resAssinatura.data);
      setInvoices(resFaturas.data.invoices || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const billing = assinatura?.billing || {};

  const copiar = async (texto, mensagem) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(mensagem);
    } catch {
      toast.error("Não consegui copiar. Selecione o texto e copie na mão.");
    }
  };

  const alternarDebito = async (ativo) => {
    try {
      const { data } = await api.put("/subscription/auto-charge", { ativo });
      setAssinatura((atual) => ({
        ...atual,
        billing: { ...atual.billing, autoCharge: data.autoCharge },
      }));
      toast.success(
        data.autoCharge
          ? "Débito automático ligado. As próximas faturas serão cobradas no cartão."
          : "Débito automático desligado. Você paga cada fatura na mão."
      );
    } catch (err) {
      toastError(err);
    }
  };

  const salvarCartao = async (e) => {
    e.preventDefault();
    setSalvandoCartao(true);
    try {
      const { data } = await api.post("/subscription/card", {
        holderName: cartao.holderName,
        number: cartao.number.replace(/\D/g, ""),
        expiryMonth: cartao.expiryMonth,
        expiryYear: cartao.expiryYear,
        ccv: cartao.ccv,
        holderInfo: {
          name: cartao.name,
          email: cartao.email,
          cpfCnpj: cartao.cpfCnpj.replace(/\D/g, ""),
          postalCode: cartao.postalCode.replace(/\D/g, ""),
          addressNumber: cartao.addressNumber,
          phone: cartao.phone.replace(/\D/g, ""),
        },
      });
      setAssinatura((atual) => ({
        ...atual,
        billing: {
          ...atual.billing,
          cardLastDigits: data.cardLastDigits,
          cardBrand: data.cardBrand,
          autoCharge: data.autoCharge,
        },
      }));
      toast.success("Cartão salvo.");
      setCartaoOpen(false);
      setCartao({
        holderName: "",
        number: "",
        expiryMonth: "",
        expiryYear: "",
        ccv: "",
        name: "",
        email: "",
        cpfCnpj: "",
        postalCode: "",
        addressNumber: "",
        phone: "",
      });
    } catch (err) {
      toastError(err);
    }
    setSalvandoCartao(false);
  };

  const removerCartao = async () => {
    try {
      await api.delete("/subscription/card");
      setAssinatura((atual) => ({
        ...atual,
        billing: {
          ...atual.billing,
          cardLastDigits: null,
          cardBrand: null,
          autoCharge: false,
        },
      }));
      toast.success("Cartão removido.");
    } catch (err) {
      toastError(err);
    }
  };

  const emAberto = invoices.filter(
    (i) => i.status === "open" || i.status === "overdue"
  );
  const totalEmAberto = emAberto.reduce((s, i) => s + Number(i.value || 0), 0);

  return (
    <MainContainer>
      <ConfirmationModal
        title="Remover o cartão salvo?"
        open={confirmRemover}
        onClose={() => setConfirmRemover(false)}
        onConfirm={removerCartao}
      >
        O débito automático desliga junto. As próximas faturas continuam sendo
        geradas — só que você passa a pagar cada uma por Pix ou boleto.
      </ConfirmationModal>

      <MainHeader>
        <Title>Financeiro</Title>
      </MainHeader>

      <div className="flex-1 space-y-6 overflow-auto px-6 pb-6">
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Seu plano, suas faturas e a forma como você paga. O que estiver em
          aberto pode ser pago por Pix na hora, sem falar com ninguém.
        </p>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Resumo
            rotulo="Plano"
            valor={assinatura?.planName || "—"}
            detalhe={CICLO[assinatura?.billingCycle] || null}
          />
          <Resumo
            rotulo="Mensalidade"
            valor={moeda(assinatura?.total)}
            detalhe="Plano, módulos e limites extras já somados"
          />
          <Resumo
            rotulo="Próximo vencimento"
            valor={dataCurta(billing.dueDate)}
            detalhe={
              billing.companyStatus === "blocked"
                ? "Acesso bloqueado por falta de pagamento"
                : null
            }
          />
          <Resumo
            rotulo="Em aberto"
            valor={moeda(totalEmAberto)}
            detalhe={`${emAberto.length} fatura(s) a pagar`}
          />
        </div>

        {/* Forma de pagamento */}
        <div className="rounded-xl border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Forma de pagamento</p>
                <p className="text-sm text-muted-foreground">
                  {billing.cardLastDigits
                    ? `${billing.cardBrand || "Cartão"} terminado em ${billing.cardLastDigits}`
                    : "Nenhum cartão salvo — hoje você paga por Pix ou boleto."}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {billing.cardLastDigits && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  aria-label="Remover cartão salvo"
                  title="Remover cartão salvo"
                  onClick={() => setConfirmRemover(true)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button variant="outline" onClick={() => setCartaoOpen(true)}>
                {billing.cardLastDigits ? "Trocar cartão" : "Cadastrar cartão"}
              </Button>
            </div>
          </div>

          {billing.cardLastDigits && (
            <div className="mt-4 flex items-center gap-3 border-t pt-4">
              <Switch
                id="auto-charge"
                checked={Boolean(billing.autoCharge)}
                onCheckedChange={alternarDebito}
              />
              <Label htmlFor="auto-charge" className="cursor-pointer">
                Cobrar no cartão automaticamente todo vencimento
                <span className="ml-1 font-normal text-muted-foreground">
                  — evita esquecer e o sistema bloquear.
                </span>
              </Label>
            </div>
          )}
        </div>

        {/* Faturas */}
        <div className="overflow-x-auto rounded-xl border bg-card">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card">
              <TableRow>
                <TableHead>Do que se trata</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="text-center">Vence em</TableHead>
                <TableHead className="text-center">Situação</TableHead>
                <TableHead className="text-center">Pagar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => {
                const situacao = SITUACAO[invoice.status] || SITUACAO.open;
                const pagavel =
                  invoice.status === "open" || invoice.status === "overdue";

                return (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">
                      {invoice.detail}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {moeda(invoice.value)}
                    </TableCell>
                    <TableCell className="text-center tabular-nums">
                      {dataCurta(invoice.dueDate)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={situacao.variante}>
                        {situacao.rotulo}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {pagavel &&
                      (invoice.pixPayload || invoice.invoiceUrl) ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPagando(invoice)}
                        >
                          <QrCode className="h-4 w-4" />
                          Pagar
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {pagavel ? "Cobrança em preparo" : "—"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && <TableRowSkeleton columns={5} />}
              {!loading && invoices.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    Nenhuma fatura por aqui ainda.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Como pagar esta fatura */}
      <Dialog
        open={Boolean(pagando)}
        onOpenChange={(aberto) => !aberto && setPagando(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pagar {moeda(pagando?.value)}</DialogTitle>
            <DialogDescription>
              {pagando?.detail} — vence em {dataCurta(pagando?.dueDate)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {pagando?.pixQrCode && (
              <div className="flex justify-center rounded-lg bg-white p-4">
                <img
                  src={`data:image/png;base64,${pagando.pixQrCode}`}
                  alt="QR Code do Pix desta fatura"
                  className="h-52 w-52"
                />
              </div>
            )}

            {pagando?.pixPayload && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  Pix copia e cola
                </Label>
                <div className="mt-1 flex gap-2">
                  <Input
                    readOnly
                    value={pagando.pixPayload}
                    className="font-mono text-xs"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Copiar código Pix"
                    onClick={() =>
                      copiar(pagando.pixPayload, "Código Pix copiado.")
                    }
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {pagando?.invoiceUrl && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() =>
                  window.open(pagando.invoiceUrl, "_blank", "noopener")
                }
              >
                <ExternalLink className="h-4 w-4" />
                Abrir boleto / página de pagamento
              </Button>
            )}

            <p className="text-xs text-muted-foreground">
              O sistema reconhece o pagamento sozinho, em geral em poucos
              minutos. Não precisa mandar comprovante.
            </p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setPagando(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cadastro de cartão */}
      <Dialog open={cartaoOpen} onOpenChange={setCartaoOpen}>
        <DialogContent className="sm:max-w-lg">
          <form onSubmit={salvarCartao}>
            <DialogHeader>
              <DialogTitle>
                <Receipt className="mr-2 inline h-5 w-5" />
                Cartão de crédito
              </DialogTitle>
              <DialogDescription>
                O número não fica guardado aqui: vai direto para o banco, que
                devolve só um código de uso. Os dados do titular são exigidos
                por ele para autorizar a cobrança.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3 py-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="holderName">Nome impresso no cartão</Label>
                <Input
                  id="holderName"
                  required
                  value={cartao.holderName}
                  onChange={(e) =>
                    setCartao({ ...cartao, holderName: e.target.value })
                  }
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="number">Número</Label>
                <Input
                  id="number"
                  required
                  inputMode="numeric"
                  autoComplete="cc-number"
                  value={cartao.number}
                  onChange={(e) =>
                    setCartao({ ...cartao, number: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="expiryMonth">Mês</Label>
                <Input
                  id="expiryMonth"
                  required
                  placeholder="08"
                  inputMode="numeric"
                  value={cartao.expiryMonth}
                  onChange={(e) =>
                    setCartao({ ...cartao, expiryMonth: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="expiryYear">Ano</Label>
                <Input
                  id="expiryYear"
                  required
                  placeholder="2030"
                  inputMode="numeric"
                  value={cartao.expiryYear}
                  onChange={(e) =>
                    setCartao({ ...cartao, expiryYear: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="ccv">Código de segurança</Label>
                <Input
                  id="ccv"
                  required
                  inputMode="numeric"
                  value={cartao.ccv}
                  onChange={(e) =>
                    setCartao({ ...cartao, ccv: e.target.value })
                  }
                />
              </div>

              <div className="sm:col-span-2 mt-2 border-t pt-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Titular do cartão
                </p>
              </div>
              <div>
                <Label htmlFor="name">Nome completo</Label>
                <Input
                  id="name"
                  required
                  value={cartao.name}
                  onChange={(e) =>
                    setCartao({ ...cartao, name: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="cpfCnpj">CPF ou CNPJ</Label>
                <Input
                  id="cpfCnpj"
                  required
                  inputMode="numeric"
                  value={cartao.cpfCnpj}
                  onChange={(e) =>
                    setCartao({ ...cartao, cpfCnpj: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={cartao.email}
                  onChange={(e) =>
                    setCartao({ ...cartao, email: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  required
                  inputMode="numeric"
                  value={cartao.phone}
                  onChange={(e) =>
                    setCartao({ ...cartao, phone: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="postalCode">CEP</Label>
                <Input
                  id="postalCode"
                  required
                  inputMode="numeric"
                  value={cartao.postalCode}
                  onChange={(e) =>
                    setCartao({ ...cartao, postalCode: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="addressNumber">Número do endereço</Label>
                <Input
                  id="addressNumber"
                  required
                  value={cartao.addressNumber}
                  onChange={(e) =>
                    setCartao({ ...cartao, addressNumber: e.target.value })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCartaoOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={salvandoCartao}>
                {salvandoCartao && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Salvar cartão
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </MainContainer>
  );
};

export default Billing;
